import React, { Component } from 'react';
import Slider, { Settings } from 'react-slick';
import SlideArrow, { Direction } from 'components/Slideshow/SlideArrow';
import SlideshowSlide from 'components/Slideshow/SlideshowSlide';
import { colors } from 'components/styles/variables';
import CoverSlide from 'components/Slideshow/CoverSlide';
import SlideshowInterface, { AdTargeting, Slide } from 'types/Slideshow';
import SlideshowDesktopAd from 'components/Slideshow/SlideshowDesktopAd';
import * as _ from 'lodash-es';
import { LEADERBOARD } from 'lib/GoogleAds/Sizes';
import Ad from 'components/Ad';
import { trackSlideshowNavigation } from 'lib/Tracking';
import { SlideshowEventType } from 'lib/EventSchema';
import { EventsDataInterface } from 'components/Article/types';

export interface DesktopSlideshowProps {
  slideshow: SlideshowInterface;
  eventsData: EventsDataInterface;
  onNext?: (idx: number, slidesLength: number, isCoverSlide?: boolean) => void;
  settings?: Partial<Settings>;
}

interface DesktopSlideshowState {
  currentAdTargeting?: AdTargeting;
  currentSlideIndex: number;
}

export default class DesktopSlideshow extends Component<
  DesktopSlideshowProps,
  DesktopSlideshowState
> {
  private slider?: Slider;

  constructor(props: DesktopSlideshowProps) {
    super(props);
    this.state = {
      currentAdTargeting: undefined,
      currentSlideIndex: 0,
    };

    this.next = this.next.bind(this);
    this.onClickNext = this.onClickNext.bind(this);
    this.onClickPrev = this.onClickPrev.bind(this);
    this.setSlider = this.setSlider.bind(this);
    this.setCurrentAdTargeting = this.setCurrentAdTargeting.bind(this);
  }

  next(idx: number, slidesLength: number, isCoverSlide = false): void {
    const { eventsData } = this.props;

    if (!this.slider) return;

    this.slider.slickNext();
    if (isCoverSlide) {
      trackSlideshowNavigation(SlideshowEventType.next_image_text, '1', eventsData);
    } else if (idx + 2 > slidesLength) {
      trackSlideshowNavigation(SlideshowEventType.next_image_text, '0', eventsData);
    } else {
      trackSlideshowNavigation(
        SlideshowEventType.next_image_text,
        (idx + 2).toString(),
        eventsData
      );
    }
    this.props.onNext?.(idx, slidesLength, isCoverSlide);
  }

  onClickNext(): void {
    const { currentSlideIndex } = this.state;
    const { slideshow, eventsData } = this.props;
    const { slides } = slideshow;

    if (!this.slider) return;

    this.slider.slickNext();
    trackSlideshowNavigation(
      SlideshowEventType.next_image_click,
      ((currentSlideIndex + 1) % (slides.length + 1)).toString(),
      eventsData
    );
  }

  onClickPrev(): void {
    const { currentSlideIndex } = this.state;
    const { slideshow, eventsData } = this.props;
    const { slides } = slideshow;
    const length = slides.length + 1;

    if (!this.slider) return;

    this.slider.slickPrev();
    trackSlideshowNavigation(
      SlideshowEventType.back_image_click,
      ((((currentSlideIndex - 1) % length) + length) % length).toString(),
      eventsData
    );
  }

  // in order to use methods that are defined on the third-party slider
  setSlider(slider: Slider): void {
    this.slider = slider;
  }

  setCurrentAdTargeting(current: number, slides: Slide[]): void {
    if (current === 0) {
      this.setState({
        currentAdTargeting: undefined,
      });
    } else {
      this.setState({
        currentAdTargeting: slides[current - 1].adTargeting,
      });
    }
  }

  render(): React.ReactNode {
    const { slideshow } = this.props;
    const { slides, coverSlide } = slideshow;

    const desktopSlides = slides.map((slide, idx) => (
      <SlideshowSlide
        slide={slide}
        index={idx}
        key={slide.photoUrl}
        total={slides.length}
        desktop={true}
        nextSlide={(): void => {
          this.next(idx, desktopSlides.length);
        }}
        adTargeting={slide.adTargeting}
      />
    ));

    const settings: Settings = {
      lazyLoad: 'ondemand',
      dots: false,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      prevArrow: <SlideArrow direction={Direction.previous} logEvent={this.onClickPrev} />,
      nextArrow: <SlideArrow direction={Direction.next} logEvent={this.onClickNext} />,
      beforeChange: (_current: number, next: number): void => {
        this.setState({ currentSlideIndex: next });
      },
      afterChange: (current: number): void => {
        this.setCurrentAdTargeting(current, slides);
      },
      ...this.props.settings,
    };

    const { currentAdTargeting } = this.state;
    const adTargetingFields = {
      make: currentAdTargeting?.make,
      model: currentAdTargeting?.model,
      year: currentAdTargeting?.year,
      body_style: currentAdTargeting?.type,
    };

    return (
      <div className="desktop-slides-ads-container">
        <div className="desktop-slides-top-ad-container">
          <div className="desktop-slides-top-ad">
            <Ad
              adConfig={{
                targeting: _.omitBy(
                  {
                    inLine: 1,
                    ...adTargetingFields,
                  },
                  _.isUndefined
                ),
                dimensions: [[728, 90]],
              }}
              placeholderSize={LEADERBOARD}
            />
          </div>
          <div className="desktop-slides">
            <Slider ref={this.setSlider} {...settings}>
              <CoverSlide
                slide={coverSlide}
                nextSlide={(): void => {
                  this.next(0, desktopSlides.length, true);
                }}
              />
              {desktopSlides}
            </Slider>
          </div>
        </div>
        <SlideshowDesktopAd
          adTargeting={this.state.currentAdTargeting || {}}
          data-testid="slide-show-desktop-ad"
        />
        <style jsx>{`
          .desktop-slides {
            max-width: 727px;
            border: 1px solid ${colors.G2};
          }

          .desktop-slides-ads-container {
            display: flex;
            flex-direction: row;
          }

          .desktop-slides-top-ad-container {
            display: flex;
            flex-direction: column;
          }

          .desktop-slides-top-ad {
            margin-bottom: 16px;
          }
        `}</style>
      </div>
    );
  }
}
